import {
  useRef,
  useEffect,
  useState,
  forwardRef,
  useImperativeHandle,
} from "react";

import { initFabricCanvas, loadBackgroundImage } from "../helpers/fabricSetup";
import {
  getTextBounds,
  isPointNearPoint,
  isPointOnLine,
} from "../helpers/hitTest";

import { createInteractionHandlers } from "../helpers/interactions";
import {
  renderFabricScene,
  setHighlightedPolygonId,
} from "../helpers/renderFabric";
import TextEditorOverlay from "./measurement/TextEditorOverlay";
import { useHistory } from "../hooks/useHistory";
import useCollabTextAnnotations from "../hooks/useCollabTextAnnotations";

import { useParams } from "react-router-dom";

const MeasurementCanvas = forwardRef(function MeasurementCanvas(props, ref) {
  const {
    activeTool,
    setActiveTool,
    image,
    scale,
    onSetScaleLength,
    thumbnailReff,
    unit,
    parentMeasurements,
    onMeasurementAdd,
    onPolygonEditStart,
    selectedObjectId,
    setSelectedObjectId,
    canvasSize,
    onCanvasSizeChange,
    imageTransforms,
    onImageTransformChange,
    onHistoryChange,
    onScreenshotRequest,
    styleSettings,
    isPanMode,
    onAddComment,
    comments,
    selectedCommentId,
    onSelectComment,
    onShowCommentInput,
    onShowCommentTooltip,
    onShowCommentThread,
    zoom,
    onZoomChange,
    displaySize,
    onDisplaySizeChange,
    updateMeasurementRunTime,
    saveCanvasReferenceForOtherComponents,
    wrapperRotation,
    colorUpdated,
    lineStyleUpdated,
    lineThicknessUpdated,
  } = props;
  const { id: projectId } = useParams(); // 👈 Extracts "id" from the route

  // DOM + Fabric refs
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const fabricCanvasRef = useRef(null);
  const fabricApiRef = useRef(null);
  const backgroundImageRef = useRef(null);
  const resizeStateRef = useRef(null); // { startX, startY, startW, startH }

  // ➕ NEW: a ref that always points to latest handlers
  const handlersRef = useRef(null);

  // Scene state
  const [isDrawing, setIsDrawing] = useState(false);
  const [currentPoints, setCurrentPoints] = useState([]);
  const [measurements, setMeasurements] = useState([]);
  const [textAnnotations, setTextAnnotations] = useState([]);
  const [guidelines, setGuidelines] = useState([]);
  const [polygons, setPolygons] = useState([]);
  const [isDraggingPolygon, setIsDraggingPolygon] = useState(false);
  const [draggedPolygonId, setDraggedPolygonId] = useState(null);
  const [draggedPointIndex, setDraggedPointIndex] = useState(null);
  const [hoveredPoint, setHoveredPoint] = useState(null);
  const [imageOffset, setImageOffset] = useState({ x: 0, y: 0 });
  const [selectedMeasurement, setSelectedMeasurement] = useState(null);
  const [selectedText, setSelectedText] = useState(null);
  const [selectedGuideline, setSelectedGuideline] = useState(null);
  const [selectedPolygon, setSelectedPolygon] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dragPoint, setDragPoint] = useState(null);
  const [isDraggingLine, setIsDraggingLine] = useState(false);
  const [draggedLineId, setDraggedLineId] = useState(null);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [scalePosition, setScalePosition] = useState({ x: 20, y: 20 });
  const [scaleLength, setScaleLength] = useState(100);
  const [isDraggingScale, setIsDraggingScale] = useState(false);
  const [isDraggingScaleEnd, setIsDraggingScaleEnd] = useState(false);
  const [isDraggingText, setIsDraggingText] = useState(false);
  const [draggedTextId, setDraggedTextId] = useState(null);
  const [isDraggingGuideline, setIsDraggingGuideline] = useState(false);
  const [draggedGuidelineId, setDraggedGuidelineId] = useState(null);
  const [isPanning, setIsPanning] = useState(false);
  const [lastPanPoint, setLastPanPoint] = useState(null);
  const [touchStartDistance, setTouchStartDistance] = useState(null);
  const [touchStartZoom, setTouchStartZoom] = useState(1);
  const [isResizingText, setIsResizingText] = useState(false);
  const [textResizeHandle, setTextResizeHandle] = useState(null);
  const [isRotatingText, setIsRotatingText] = useState(false);
  const [scaleAngle, setScaleAngle] = useState(0);
  const [isResizingCanvas, setIsResizingCanvas] = useState(false);
  const [resizeStart, setResizeStart] = useState({ x: 0, y: 0 });
  const [resizeStartSize, setResizeStartSize] = useState({
    width: 0,
    height: 0,
  });
  const [isOpenPolygonMode, setIsOpenPolygonMode] = useState(false);
  const [highlightedId, setHighlightedId] = useState(null);
  const [highlightTick, setHighlightTick] = useState(0);
  const [dots, setDots] = useState([]);

  const [selectedDot, setSelectedDot] = useState(null);
  const [isDraggingDot, setIsDraggingDot] = useState(false);
  const [draggedDotId, setDraggedDotId] = useState(null);
  const [dotDragOffset, setDotDragOffset] = useState({ x: 0, y: 0 });

  // state
  const [arrows, setArrows] = useState([]); // [{id, start:{x,y}, end:{x,y}, color}]
  const [selectedArrowId, setSelectedArrowId] = useState(null);
  // dragging flags (add useState hooks near other dragging flags)
  const [isDraggingArrow, setIsDraggingArrow] = useState(false);
  const [draggedArrowId, setDraggedArrowId] = useState(null);
  const [arrowDragMode, setArrowDragMode] = useState(null);

  useEffect(() => {
    if (containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      // onDisplaySizeChange({ width: rect.width, height: rect.height });
    }
  }, []);

  // History
  const { history, index, addToHistory, undo, redo, resetHistory } = useHistory(
    {
      measurements: [],
      textAnnotations: [],
      guidelines: [],
      polygons: [],
      scalePosition: { x: 20, y: 20 },
      scaleLength: 100,
      scaleAngle: 0,
    }
  );
  // Build handlers (re-created each render with latest state)
  const handlers = createInteractionHandlers({
    canvasRef,
    activeTool,
    isPanMode,
    scale,
    unit,
    comments,
    canvasSize,
    displaySize,
    onDisplaySizeChange,
    onCanvasSizeChange,
    zoom,
    imageTransforms,
    setActiveTool,
    onMeasurementAdd,
    onShowCommentInput,
    onShowCommentThread,
    onSelectComment,
    onShowCommentTooltip,
    onZoomChange,
    selectedObjectId,
    setSelectedObjectId,
    addToHistory,
    updateMeasurementRunTime,
    fabricCanvasRef,
    onPolygonEditStart,
    state: {
      measurements,
      setMeasurements,
      textAnnotations,
      setTextAnnotations,
      guidelines,
      setGuidelines,
      polygons,
      setPolygons,

      dots,
      setDots,
      selectedDot,
      setSelectedDot,
      isDraggingDot,
      setIsDraggingDot,
      draggedDotId,
      setDraggedDotId,
      dotDragOffset,
      setDotDragOffset,

      arrows,
      setArrows,
      selectedArrowId,
      setSelectedArrowId,

      isDraggingArrow,
      setIsDraggingArrow,
      draggedArrowId,
      setDraggedArrowId,
      arrowDragMode,
      setArrowDragMode,

      currentPoints,
      setCurrentPoints,
      isDrawing,
      setIsDrawing,

      selectedMeasurement,
      setSelectedMeasurement,
      selectedText,
      setSelectedText,
      selectedGuideline,
      setSelectedGuideline,
      selectedPolygon,
      setSelectedPolygon,

      hoveredPoint,
      setHoveredPoint,

      imageOffset,
      setImageOffset,
      isPanning,
      setIsPanning,
      lastPanPoint,
      setLastPanPoint,
      touchStartDistance,
      setTouchStartDistance,
      touchStartZoom,
      setTouchStartZoom,

      isDragging,
      setIsDragging,
      dragPoint,
      setDragPoint,
      isDraggingLine,
      setIsDraggingLine,
      draggedLineId,
      setDraggedLineId,

      dragOffset,
      setDragOffset,

      scalePosition,
      setScalePosition,
      scaleLength,
      setScaleLength,
      scaleAngle,
      setScaleAngle,
      isDraggingScale,
      setIsDraggingScale,
      isDraggingScaleEnd,
      setIsDraggingScaleEnd,

      isDraggingText,
      setIsDraggingText,
      draggedTextId,
      setDraggedTextId,
      isResizingText,
      setIsResizingText,
      textResizeHandle,
      setTextResizeHandle,
      isRotatingText,
      setIsRotatingText,

      isDraggingGuideline,
      setIsDraggingGuideline,
      draggedGuidelineId,
      setDraggedGuidelineId,

      isDraggingPolygon,
      setIsDraggingPolygon,
      draggedPolygonId,
      setDraggedPolygonId,
      draggedPointIndex,
      setDraggedPointIndex,

      isResizingCanvas,
      setIsResizingCanvas,
      resizeStart,
      setResizeStart,
      resizeStartSize,
      setResizeStartSize,
      isOpenPolygonMode,
      setIsOpenPolygonMode,
    },
  });
  // ➕ keep handlersRef always pointing to latest functions
  useEffect(() => {
    handlersRef.current = handlers;
  }, [handlers]);

  // Init Fabric + load background on image/size changes
  useEffect(() => {
    if (!canvasRef.current || !image) return;

    let fc;
    (async () => {
      if (fabricCanvasRef.current) {
        try {
          fabricCanvasRef.current.dispose();
        } catch {}
      }

      // ⚠️ pass PROXIES that call the latest handlers via ref
      const { canvas, fabric } = await initFabricCanvas(
        canvasRef.current,
        canvasSize,
        {
          onMouseDown: (e, p) => handlersRef.current?.handleMouseDown(e, p),
          onMouseMove: (e, p) => handlersRef.current?.handleMouseMove(e, p),
          onMouseUp: () => handlersRef.current?.handleMouseUp(),
          onWheel: (opt) => handlersRef.current?.handleWheel(opt),
          onTouchStart: (e) => handlersRef.current?.handleTouchStart(e),
          onTouchMove: (e) => handlersRef.current?.handleTouchMove(e),
          onTouchEnd: () => handlersRef.current?.handleTouchEnd(),
        }
      );

      fabricCanvasRef.current = canvas;
      fabricApiRef.current = fabric;
      if (handlersRef.current && handlersRef.current.ctx) {
        handlersRef.current.ctx.canvas = fabricCanvasRef.current;
        handlersRef.current.ctx.fabric = fabricApiRef.current;
      }
      fc = canvas;

      try {
        const bg = await loadBackgroundImage(
          canvas,
          fabric,
          image,
          imageTransforms,
          displaySize,
          onCanvasSizeChange
        );

        backgroundImageRef.current = bg;
        saveCanvasReferenceForOtherComponents(canvas);
      } catch (err) {
        console.error("Background load failed:", err);
      }
    })();

    return () => {
      if (fc) {
        try {
          fc.dispose();
        } catch {}
      }
      fabricCanvasRef.current = null;
      fabricApiRef.current = null;
    };
  }, [image, imageTransforms, canvasSize.width, canvasSize.height]);

  // Render scene objects whenever state changes
  useEffect(() => {
    const fc = fabricCanvasRef.current;
    const fabric = fabricApiRef.current;
    if (!fc || !fabric) return;

    renderFabricScene({
      fabricCanvas: fc,
      fabric,
      measurements,
      textAnnotations,
      guidelines,
      polygons,
      comments,
      dots,
      selectedDot,
      arrows,
      selectedArrowId,
      currentPoints,
      hoveredPoint,
      selectedMeasurement,
      selectedText,
      selectedGuideline,
      selectedPolygon,
      selectedCommentId,
      styleSettings,
      activeTool,
      unit,
      scale,
      canvasSize,
      scalePosition,
      scaleLength,
      scaleAngle,
      isPointNearPoint,
      isPointOnLine,
    });
  }, [measurements, textAnnotations, colorUpdated, lineStyleUpdated, lineThicknessUpdated, guidelines, polygons, comments, dots, selectedDot, arrows, selectedArrowId, currentPoints, hoveredPoint, selectedMeasurement, selectedText, selectedGuideline, selectedPolygon, selectedCommentId, styleSettings, activeTool, unit, scale, canvasSize, scalePosition, scaleLength, scaleAngle, highlightTick]);
  // Undo/redo availability
  useEffect(() => {
    onHistoryChange?.(index > 0, index < history.length - 1);
  }, [index, history.length, onHistoryChange]);

  const deleteSelectionFromState = () => {
    const canvas = fabricCanvasRef.current;
    // 1) measurements (lines/angles)
    if (selectedMeasurement && selectedMeasurement !== "scale") {
      setMeasurements((prev) =>
        prev.filter((m) => m.id !== selectedMeasurement)
      );
      setSelectedMeasurement(null);
      addToHistory?.();
    }
    // 2) optional: delete the scale line if it's the selected thing
    else if (selectedMeasurement === "scale") {
      // If you prefer to block deleting scale, just remove this branch.
      setScalePosition({ x: 20, y: 20 });
      setScaleLength(100);
      setScaleAngle(0);
      setSelectedMeasurement(null);
      addToHistory?.();
    }
    // 3) text
    else if (selectedText) {
      setTextAnnotations((prev) => {
        const next = prev.filter((t) => t.id !== selectedText);
        syncTextAnnotations(next); // ← broadcast the updated array
        return next;
      });
      setSelectedText(null);
      addToHistory?.();
    }
    // 4) guidelines
    else if (selectedGuideline) {
      setGuidelines((prev) => prev.filter((g) => g.id !== selectedGuideline));
      setSelectedGuideline(null);
      addToHistory?.();
    }
    // 5) polygons
    else if (selectedPolygon) {
      setPolygons((prev) => prev.filter((p) => p.id !== selectedPolygon));
      setSelectedPolygon(null);
      addToHistory?.();
    }

    // 6) if user was midway drawing something, cancel it
    else if (isDrawing || (currentPoints && currentPoints.length > 0)) {
      setIsDrawing(false);
      setCurrentPoints([]);
    }
    // 6) dots
    else if (selectedDot) {
      setDots((prev) => prev.filter((d) => d.id !== selectedDot));
      setSelectedDot(null);
      addToHistory?.();
    }
    // 6) arrow
    else if (selectedArrowId) {
      setArrows((prev) => prev.filter((a) => a.id !== selectedArrowId));
      setSelectedArrowId(null);
      addToHistory?.();
    }

    // Clear Fabric's own active object (if any) and redraw
    try {
      canvas?.discardActiveObject();
      canvas?.requestRenderAll();
    } catch {}
  };

  // ADD: full scene reset that keeps the background image (unless you ask to reset it)
  const resetScene = ({ resetBg = false, clearBgFilters = true } = {}) => {
    const canvas = fabricCanvasRef.current;
    if (!canvas) return;

    const bg = canvas.backgroundImage || null;

    // 1) Clear selection + remove overlay objects (background is not in getObjects)
    canvas.discardActiveObject();
    const objs = canvas.getObjects();

    if (objs && objs.length) canvas.remove(...objs);

    // 2) Reset viewport (zoom/pan)
    canvas.setViewportTransform([1, 0, 0, 1, 0, 0]);

    // 3) Clean/optionally reset background (but DO NOT remove it)
    if (bg) {
      if (clearBgFilters) {
        bg.filters = [];
        bg.applyFilters?.();
      }
      if (resetBg) {
        bg.set?.({
          originX: "left",
          originY: "top",
          left: 0,
          top: 0,
          angle: 0,
          scaleX: 1,
          scaleY: 1,
          skewX: 0,
          skewY: 0,
          flipX: false,
          flipY: false,
        });
        if ("transformMatrix" in bg) bg.transformMatrix = null;
        bg.setCoords?.();
      }
    }

    // 4) Force redraw (Fabric v5/v6 safe)
    if (bg && typeof canvas.setBackgroundImage === "function") {
      canvas.setBackgroundImage(bg, () => canvas.requestRenderAll());
    } else {
      if (bg) {
        canvas.backgroundImage = bg;
        bg.dirty = true;
      }
      canvas.requestRenderAll();
    }

    // 5) Clear ALL React-side scene state so nothing re-renders back
    setMeasurements([]);
    setTextAnnotations([]);
    setGuidelines([]);
    setPolygons([]);
    setCurrentPoints([]);

    setSelectedMeasurement(null);
    setSelectedText(null);
    setSelectedGuideline(null);
    setSelectedPolygon(null);
    setHoveredPoint(null);

    setIsDrawing(false);
    setIsDragging(false);
    setIsDraggingLine(false);
    setDraggedLineId(null);
    setDragPoint(null);
    setDragOffset({ x: 0, y: 0 });

    setIsDraggingText(false);
    setDraggedTextId(null);
    setIsResizingText(false);
    setTextResizeHandle(null);
    setIsRotatingText(false);

    setIsDraggingGuideline(false);
    setDraggedGuidelineId(null);

    setIsDraggingPolygon(false);
    setDraggedPolygonId(null);
    setDraggedPointIndex(null);

    setIsPanning(false);
    setLastPanPoint(null);
    setTouchStartDistance(null);
    setTouchStartZoom(1);

    // reset measurement scale helpers in-canvas
    setScalePosition({ x: 20, y: 20 });
    setScaleLength(100);
    setScaleAngle(0);

    setDots([]);
    setSelectedDot(null);
    setIsDraggingDot(false);
    setDraggedDotId(null);
    setDotDragOffset({ x: 0, y: 0 });
    setArrows([]);
    setSelectedArrowId(null);
    setIsDraggingArrow(null);
    setDraggedArrowId(null);
    setArrowDragMode(null);

    // 6) Reset history to the initial baseline
    resetHistory?.();
  };

  // 1) classify helpers
  const isPolygonItem = (it) => Array.isArray(it?.points);
  const isLineItem = (it) => it?.start && it?.end;
  const isAngleItem = (it) => it?.center && it?.point1 && it?.point2;

  // 2) merge current child state (measurements + polygons) into ONE array
  const getMergedMeasurements = () => {
    const m = Array.isArray(measurements) ? measurements : [];
    const p = Array.isArray(polygons) ? polygons : [];
    // tag is optional; you can omit _kind if you want
    const mWrapped = m.map((x) => ({ ...x }));
    const pWrapped = p.map((x) => ({ ...x }));
    return [...mWrapped, ...pWrapped];
  };

  // 3) split ONE array back into child state buckets
  const loadFromMergedMeasurements = (arr = []) => {
    const m = [];
    const p = [];
    for (const item of Array.isArray(arr) ? arr : []) {
      if (isPolygonItem(item)) p.push(item);
      else if (isLineItem(item) || isAngleItem(item)) m.push(item);
      else m.push(item); // fallback: keep unknowns in measurements
    }

    setMeasurements(m);
    setPolygons(p);

    // your existing effect will re-render everything
  };

  // TEXT SNAPSHOT HELPERS
  const getTextAnnotationsSnapshot = () => {
    // don’t persist transient UI flags
    return (Array.isArray(textAnnotations) ? textAnnotations : []).map((a) => ({
      id: a.id,
      position: a.position,
      text: a.text,
      width: a.width,
      height: a.height,
      rotation: a.rotation ?? 0,
      padding: a.padding ?? 0,
      showTextBoxBorder: !!a.showTextBoxBorder,
      textboxBorderColor: a.textboxBorderColor ?? "black",
      fill: a.fill ?? "yellow",
      isEditing: false, // always off when saved
    }));
  };

  const loadTextAnnotationsSnapshot = (arr = []) => {
    const clean = (Array.isArray(arr) ? arr : []).map((a) => ({
      ...a,
      isEditing: false, // ensure overlay is closed on load
    }));
    setTextAnnotations(clean);
  };

  const onOpenPolygonClicked = () => {
    if (!selectedObjectId || !polygons?.length) return;

    setPolygons((prev) =>
      prev.map((poly) => {
        if (poly.id === selectedObjectId) {
          const updatedPoints = [...(poly.points || [])];
          // remove the last point if it exists
          if (updatedPoints.length > 0) updatedPoints.pop();

          return {
            ...poly,
            isClosed: false,
            points: updatedPoints,
          };
        }
        return poly;
      })
    );

    console.log(
      "🔓 Polygon reopened and last point removed:",
      selectedObjectId
    );
  };
  // inside MeasurementCanvas.jsx (where you already have useImperativeHandle)
  const setMeasurementsOnly = (arr = []) => {
    setMeasurements(Array.isArray(arr) ? arr : []);
    // ensure canvas repaints after React state update
    requestAnimationFrame(() => {
      try {
        renderFabricScene?.();
      } catch {}
      try {
        fabricCanvasRef.current?.requestRenderAll?.();
      } catch {}
    });
  };

  useEffect(() => {
    setSelectedMeasurement(null);
    setSelectedPolygon(null);
  }, [colorUpdated, lineStyleUpdated, lineThicknessUpdated]);

  // expose imperative API to parent (Toolbar calls this)
  useImperativeHandle(ref, () => ({
    deleteSelection: deleteSelectionFromState,
    resetScene,
    // return the entire measurements array (lines + angles + polygons)
    // snapshot returns the ONE merged array (what you store in Redux)
    getMergedMeasurements,
    // loader accepts the ONE merged array and splits internally
    loadFromMergedMeasurements,
    setMeasurementsOnly,
    //text anotation
    getTextAnnotations: getTextAnnotationsSnapshot,
    loadTextAnnotations: loadTextAnnotationsSnapshot,
    getCanvasClientRect: () => canvasRef.current?.getBoundingClientRect?.(),
    // NEW: append polygons from outside (parent)

    appendPolygons: (incoming = []) => {
      const arr = Array.isArray(incoming) ? incoming : [incoming];
      setPolygons((prev) => {
        const existing = new Set(prev.map((p) => p.id));
        const toAdd = arr
          .filter((p) => p && p.id && !existing.has(p.id))
          .map((p) => ({ ...p, isImport: true })); // ← ensure imported = true
        return toAdd.length ? [...prev, ...toAdd] : prev;
      });
    },
    onOpenPolygonClicked,
    enableOpenPolygonMode: () => setIsOpenPolygonMode(true),

    clearImportedPolygons: () => {
      // drop imported polygons from the child state
      setPolygons((prev) => prev.filter((p) => p?.isImport !== true));
      // if you also mirror polygons in the child `measurements` array, keep this:
      setMeasurements((prev) => prev.filter((m) => m?.isImport !== true));
      // and re-render
    },
    highlightPolygon: (idOrNull) => {
      setHighlightedPolygonId(idOrNull || null); // tell renderer which id to highlight
      setHighlightTick((n) => n + 1); // force a re-render pass
    },
    removeById: (id) => {
      if (!id) return;
      const canvas = fabricCanvasRef.current;
      // drop from canvas-owned states that drive renderFabric
      setPolygons((prev) => prev.filter((p) => p.id !== id));
      setMeasurements((prev) => prev.filter((m) => m.id !== id));

      // force a quick repaint (optional if your render effect runs on state change)
      canvas.requestRenderAll?.();
    },
    // read-only snapshot
    getGuidelines: () => [...guidelines],

    // (optional) loader if you ever want to restore them
    loadGuidelines: (arr = []) => {
      const safe = Array.isArray(arr) ? arr : [];
      setGuidelines(safe.map((g) => ({ ...g })));
    },

    setScaleOverlay: ({ position, length, angle } = {}) => {
      if (
        position &&
        Number.isFinite(position.x) &&
        Number.isFinite(position.y)
      ) {
        setScalePosition({ x: position.x, y: position.y });
      }
      if (Number.isFinite(length)) setScaleLength(length);
      if (Number.isFinite(angle)) setScaleAngle(angle);
      try {
        fabricCanvasRef.current?.requestRenderAll?.();
      } catch {}
    },
    undoLastPolygonPoint: () => {
      setCurrentPoints((prev) => {
        if (!prev || prev.length === 0) return prev; // nothing to undo
        const updated = [...prev.slice(0, -1)]; // remove last point
        // if no points left, exit polygon drawing mode
        if (updated.length === 0) {
          setIsDrawing(false);
          setActiveTool("select");
        }
        return updated;
      });
    },
    getDots: () => [...dots],

    loadDots: (arr = []) =>
      setDots(Array.isArray(arr) ? arr.map((d) => ({ ...d })) : []),

    getArrows: () => [...arrows],

    loadArrows: (arr = []) =>
      setArrows(Array.isArray(arr) ? arr.map((d) => ({ ...d })) : []),
  }));

  const { syncTextAnnotations } = useCollabTextAnnotations(
    projectId,
    (remote) => {
      // Load whole array from room. Keep isEditing false on remote load.
      const safe = Array.isArray(remote) ? remote : [];
      setTextAnnotations(safe.map((a) => ({ ...a, isEditing: false })));
    }
  );

  useEffect(() => {
    // notify parent on any change; parent decides if/what to broadcast
    props.onScaleOverlayChange?.({
      position: scalePosition,
      length: scaleLength,
      angle: scaleAngle,
    });
    // depend on scalars to avoid spurious calls
  }, [scalePosition.x, scalePosition.y, scaleLength, scaleAngle]);

  useEffect(() => {
    if (!fabricCanvasRef.current) return;
    const canvas = fabricCanvasRef.current;

    canvas.defaultCursor = isOpenPolygonMode ? "cell" : "default";
    canvas.requestRenderAll();
  }, [isOpenPolygonMode]);

  useEffect(() => {
    onSetScaleLength(scaleLength);
  }, [scaleLength]);

  // position of the  wrapper that contains the canvas
  const [winPos, setWinPos] = useState({ x: 0, y: 0 });
  const dragRef = useRef({ on: false, sx: 0, sy: 0, px: 0, py: 0 });

  // keep latest tool in a ref so window listeners see updates
  const activeToolRef = useRef(activeTool);
  useEffect(() => {
    activeToolRef.current = activeTool;
    // if user switches tools mid-drag, end the drag immediately
    if (activeTool !== "pan" && dragRef.current.on) {
      dragRef.current.on = false;
      document.body.style.cursor = "";
    }
  }, [activeTool]);

  // RAF throttle for smoothness
  const rafRef = useRef(null);
  const pendingPosRef = useRef(null);
  const flushRaf = () => {
    if (pendingPosRef.current) {
      setWinPos(pendingPosRef.current);
      pendingPosRef.current = null;
    }
    rafRef.current = null;
  };

  // start drag on mousedown
  const onDragStart = (e) => {
    e.preventDefault?.();
    if (activeToolRef.current !== "pan") return;
    dragRef.current = {
      on: true,
      sx: e.clientX,
      sy: e.clientY,
      px: winPos.x,
      py: winPos.y,
    };
    document.body.style.cursor = "grabbing";
  };

  useEffect(() => {
    const onMove = (e) => {
      if (!dragRef.current.on) return;
      if (activeToolRef.current !== "pan") return;

      const dx = e.clientX - dragRef.current.sx;
      const dy = e.clientY - dragRef.current.sy;
      const next = { x: dragRef.current.px + dx, y: dragRef.current.py + dy };

      // throttle to 1 setState per frame
      pendingPosRef.current = next;
      if (!rafRef.current) {
        rafRef.current = requestAnimationFrame(flushRaf);
      }
    };

    const onUp = () => {
      if (!dragRef.current.on) return;
      dragRef.current.on = false;
      document.body.style.cursor = "";
    };

    window.addEventListener("mousemove", onMove, { passive: false });
    window.addEventListener("mouseup", onUp, { passive: true });
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, []);

  // keep Fabric hit-testing synced to the new DOM position (after transform)
  useEffect(() => {
    const id = requestAnimationFrame(() => {
      const fc = fabricCanvasRef.current;
      fc?.calcOffset?.();
      fc?.requestRenderAll?.();
    });
    return () => cancelAnimationFrame(id);
  }, [winPos.x, winPos.y]);

  // called only on SAVE
  const handleTextChange = (id, value) => {
    setTextAnnotations((prev) => {
      const next = prev.map((a) => (a.id === id ? { ...a, text: value } : a));
      syncTextAnnotations(next);
      return next;
    });
  };

  const handleTextCommit = (id) => {
    setTextAnnotations((prev) => {
      const next = prev.map((a) =>
        a.id === id ? { ...a, isEditing: false } : a
      );
      syncTextAnnotations(next);
      return next;
    });
    setSelectedText(id); // optional: keep selected after saving
    addToHistory?.(); // record a snapshot
  };

  // called on CANCEL (no content change)
  const handleTextCancel = (id) => {
    setTextAnnotations((prev) => {
      const next = prev.map((a) =>
        a.id === id ? { ...a, isEditing: false } : a
      );
      syncTextAnnotations(next);
      return next;
    });
  };
  const setBothRefs = (el) => {
    canvasRef.current = el;
    thumbnailReff.current = el;
  };

  return (
    <div
      ref={containerRef}
      className="flex-1 overflow-auto relative"
      tabIndex={0}
    >
      <div
        id="canvas-container"
        style={{
          width: `${displaySize.width}px`,
          height: window.innerHeight - 100,
          margin: "0 auto",
          willChange: "transform",
          overflow: "visible",
        }}
      >
        <div
          onMouseDown={onDragStart}
          style={{
            position: "absolute",
            left: 0,
            top: 0,
            width: `${displaySize.width}px`,
            height: `${displaySize.height}px`,
            transform: `translate3d(${winPos.x}px, ${winPos.y}px, 0) `, // <-- moves whole canvas
            willChange: "transform",
            background: "transparent",
            cursor:
              activeTool === "pan"
                ? dragRef.current.on
                  ? "grabbing"
                  : "grab"
                : "default",
            userSelect: "none",
            userSelect: "none",
            zIndex: 2, // on top to receive drags
          }}
        >
          <canvas
            ref={setBothRefs}
            width={canvasSize.width}
            height={canvasSize.height}
            style={{
              display: "block",
              margin: "0 auto",
              width: `${displaySize.width}px`,
              height: `${displaySize.height}px`,
              background: "transparent",
            }}
          />
        </div>
      </div>

      <TextEditorOverlay
        textAnnotations={textAnnotations}
        selectedText={selectedText}
        displaySize={displaySize}
        canvasSize={canvasSize}
        getTextBounds={getTextBounds}
        onChange={handleTextChange}
        onCommit={handleTextCommit}
        onCancel={handleTextCancel}
      />
    </div>
  );
});
export default MeasurementCanvas;
